// @givmo/employer-module — the Tax documents tile: the annual reports and the tax lines, one tile
// (EP-WEB26).
//
// The Tax documents tile leads the page (EP-WEB26, Jonathan's ruling 2026-09-29, Q4: every customer
// needs it every year, so it is the easiest to reach), and the separate Tax lines tile merges into it
// (Q1). So this tile holds the two things a company needs for its taxes, and says which is which:
//
//   - THE ANNUAL REPORTS (PDF) are the tile's primary action, as every tile's one action is: the portal
//     VIEWS its list further down this page; the company console OPENS the Organization's Tax documents
//     page. The whole tile is its hit area (the action's `::after` layer), and the tile states whether
//     the newest report is ready.
//   - THE TAX LINES (CSV) are a second, clearly separate action: the spreadsheet of the same deductible
//     lines, for the year its picker names. They sit in their own zone, marked by a leading rule, ABOVE
//     the primary's layer (`relative z-[1]`, inside the tile's own stacking context), so neither the
//     picker nor the spreadsheet button ever runs the primary — and nothing here nests a control inside
//     another. The zone keeps the tax lines' description word for word: it is tax copy, counsel-gated.
//
// The spreadsheet is EP-WEB7's tax export, unchanged: the same endpoint, year and file
// (`downloadTaxLinesCsv`), and the same test ids (`exports-row-tax` on its zone, `exports-run-tax`,
// `exports-tax-year`, `exports-status-tax`), so a spec that drove the old tile drives this one.
//
// The tile spans two columns from `sm` up, which is what lets the grid of five fill cleanly: at three
// columns, Tax documents and the year recap make the first row and the three other exports the second.

import { useId } from 'react';
import { Button, Icon, Select, cn, testIdProp } from '@givmo/design-system';
import { Check, Download, FileText, LoaderCircle, RotateCcw } from 'lucide-react';
import type { EmployerModuleDoor } from '../../lib/module-paths';
import {
  ExportStatusLine,
  OpenAction,
  TILE_SHELL_CLASS,
  ViewAction,
  exportActionName,
  type ExportState,
} from './report-tile';

/** The tax lines' description — counsel-gated tax copy, word for word as EP-WEB7 shipped it. */
export const TAX_LINES_DESCRIPTION =
  "Your deductible contribution lines for one tax year — the same lines as that year's annual report.";

/** What the spreadsheet button says, and which glyph it carries, in each state. */
const CSV_LOOK: Record<ExportState['kind'], { label: string; glyph: typeof Download }> = {
  idle: { label: 'Spreadsheet (CSV)', glyph: Download },
  preparing: { label: 'Preparing…', glyph: LoaderCircle },
  ready: { label: 'Ready — Save', glyph: Download },
  saved: { label: 'Downloaded', glyph: Check },
  nothing: { label: 'Spreadsheet (CSV)', glyph: Download },
  failed: { label: 'Try again', glyph: RotateCcw },
};

export interface TaxDocumentsTileProps {
  /** Where the annual reports live: `null` → this page's own list (the portal); a door → the console's page. */
  door: EmployerModuleDoor | null;
  /** The annual reports' state in one phrase ("2025 report ready"), or `null` while unknown. */
  readiness: string | null;
  /** The in-page anchor of the list, for the portal's VIEW. */
  anchor: string;
  taxYear: string;
  taxYearOptions: { value: string; label: string }[];
  onTaxYearChange: (year: string | null) => void;
  /** The tax-lines spreadsheet's last attempt. */
  csv: ExportState;
  onRunCsv: () => void;
  /** Saves the spreadsheet waiting on the tile (state `ready`). */
  onSaveCsv: () => void;
}

export function TaxDocumentsTile({
  door,
  readiness,
  anchor,
  taxYear,
  taxYearOptions,
  onTaxYearChange,
  csv,
  onRunCsv,
  onSaveCsv,
}: TaxDocumentsTileProps) {
  const statusId = useId();
  const look = CSV_LOOK[csv.kind];
  const stated = csv.kind === 'failed' || csv.kind === 'nothing';

  return (
    <li
      className={cn(
        TILE_SHELL_CLASS,
        'grid gap-y-14 p-14 sm:col-span-2 sm:grid-cols-2 sm:gap-x-20 sm:gap-y-0 sm:p-20',
      )}
      {...testIdProp('reports-tile-tax-documents')}
    >
      {/* The annual reports: the tile's primary action. */}
      <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-12 gap-y-2 sm:grid-rows-[auto_1fr_auto] sm:items-start sm:gap-y-10">
        <span className="col-[1] row-[1/4] flex size-36 items-center justify-center self-start rounded-lg bg-brand-50 text-brand-600 sm:row-[1/2]">
          <Icon icon={FileText} size={18} aria-hidden />
        </span>
        <p className="col-[2] row-[1/2] min-w-0 font-sans text-s15 font-w600 tracking-givmo text-text-strong sm:col-[2/4] sm:self-center">
          Tax documents
        </p>
        <p className="col-[2] row-[2/3] font-sans text-s13 leading-h15 tracking-normal text-text-tertiary sm:col-[1/4]">
          {door === null
            ? 'Your company’s annual donation reports.'
            : 'Your company’s annual donation reports, kept with your organization’s records.'}
        </p>
        <p
          className="col-[2] row-[3/4] min-w-0 pt-2 font-sans text-s12 font-w500 tracking-normal text-text-secondary sm:col-[1/3] sm:self-end sm:pt-6"
          {...testIdProp('reports-tile-tax-documents-meta')}
        >
          {readiness === null ? 'PDF' : `PDF · ${readiness}`}
        </p>
        <div className="col-[3] row-[1/4] flex items-center self-center sm:row-[3/4] sm:self-end sm:justify-self-end sm:pt-6">
          {door === null ? (
            <ViewAction
              anchor={anchor}
              label="View tax documents"
              testId="reports-tile-tax-documents-view"
            />
          ) : (
            <OpenAction door={door} testId="reports-tax-documents-link" />
          )}
        </div>
      </div>

      {/* The tax lines: a second action of its own, above the primary's layer, marked by a leading
          rule (a rule, never a fill: a tint reads as pressed or disabled). */}
      <div
        className="relative z-[1] flex min-w-0 flex-col gap-6 border-t border-border-subtle pt-14 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-20"
        aria-busy={csv.kind === 'preparing' || undefined}
        {...testIdProp('exports-row-tax')}
      >
        {/* `sm:pt-8`: on the same line as the tile's name, which is centred on its 36px glyph. */}
        <p className="font-sans text-s13 font-w600 tracking-givmo text-text-strong sm:pt-8">
          Tax lines
        </p>
        <p className="font-sans text-s13 leading-h15 tracking-normal text-text-tertiary">
          {TAX_LINES_DESCRIPTION}
        </p>
        <ExportStatusLine state={csv} id={statusId} testId="exports-status-tax" />
        <div className="mt-auto flex flex-wrap items-center gap-8 pt-6">
          <Select
            value={taxYear}
            onValueChange={onTaxYearChange}
            options={taxYearOptions}
            appearance="saas"
            aria-label="Tax year"
            className="w-[92px]"
            testId="exports-tax-year"
          />
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            disabled={csv.kind === 'preparing'}
            leadingIcon={
              <Icon
                icon={look.glyph}
                size={14}
                className={cn(
                  csv.kind === 'preparing' && 'animate-spin motion-reduce:animate-none',
                  csv.kind === 'saved' && 'text-success-700',
                )}
                aria-hidden
              />
            }
            onClick={csv.kind === 'ready' ? onSaveCsv : onRunCsv}
            // Names the file, and the year the picker beside it chose; carries the visible words.
            aria-label={exportActionName(
              csv.kind,
              `the tax lines spreadsheet (CSV) for ${taxYear}`,
            )}
            aria-describedby={stated ? statusId : undefined}
            testId="exports-run-tax"
          >
            {look.label}
          </Button>
        </div>
      </div>
    </li>
  );
}
